import { useState, useEffect } from 'react'
import ChartMenu, { MenuRow, MenuSelect, MenuWeight, MenuSwatch, MenuDivider, MenuActions, MenuButton, MenuIndex } from './menu/ChartMenu'

// Same contract as SRLineSettingsMenu: every control commits immediately.
// There is no staged draft and no Apply — the line on the chart is the
// draft. `vwap` is the object the menu opened with (the parent updates the
// ref without refreshing this prop), so patches spread over it rather than
// over a copy that never arrives.
//
// Frame, fit, outside-click/Esc and every style: menu/ChartMenu + the
// CHART MENUS block in styles.css.

const LINE_WEIGHTS = [1, 2, 3, 4]
// The stored value stays 'vwap' — that is what the chart creates, what
// solcheddar_avwap_defaults holds, and what every saved AVWAP already has.
// Renaming it to 'ohlc4' changed nothing in the painter (it branches
// high/low/else) but rewrote the default and busted each line's accum cache
// on first edit. Only the LABEL was wrong: the else branch averages
// (O+H+L+C)/4, so that is what it now says.
const MODES = [
  { value: 'vwap', label: 'OHLC4' },
  { value: 'high', label: 'High' },
  { value: 'low',  label: 'Low' },
]

function shownMode(mode) {
  if (mode === 'high' || mode === 'low') return mode
  return 'vwap'
}

export default function AVWAPSettingsMenu({ show, x, y, vwap, onUpdate, onDelete, onClose }) {
  // Fallbacks match what the chart actually creates (and re-saves as the
  // defaults), so a line missing either field doesn't change colour or
  // width just by opening this menu.
  const [color, setColor] = useState(vwap?.color || '#ffaa00')
  const [weight, setWeight] = useState(vwap?.weight || 2)
  const [mode, setMode] = useState(() => shownMode(vwap?.mode))

  useEffect(() => {
    if (show && vwap) {
      setColor(vwap.color || '#ffaa00')
      setWeight(vwap.weight || 2)
      setMode(shownMode(vwap.mode))
    }
  }, [show, vwap])

  const apply = (patch) => {
    const next = { color, weight, mode, ...patch }
    if ('color' in patch) setColor(next.color)
    if ('weight' in patch) setWeight(next.weight)
    if ('mode' in patch) setMode(next.mode)
    onUpdate({
      ...vwap,
      color: next.color,
      weight: next.weight,
      // Anything other than high/low takes the OHLC4 branch in the painter.
      mode: next.mode === 'high' || next.mode === 'low' ? next.mode : 'vwap',
    })
  }

  if (!show || !vwap) return null

  return (
    <ChartMenu anchor={{ x, y }} onClose={onClose}>
      <MenuRow>
        <MenuSelect value={mode} options={MODES} title="Price used in the average"
          onChange={(v) => apply({ mode: v })} />
        <MenuDivider />
        <MenuSwatch value={color} onChange={c => apply({ color: c })} />
        <MenuDivider />
        <MenuWeight value={weight} weights={LINE_WEIGHTS} onChange={(w) => apply({ weight: w })} />
      </MenuRow>
      <MenuIndex value={vwap.index} onChange={(v) => { vwap.index = v; onUpdate({ ...vwap, index: v }) }} />
      <MenuActions>
        <MenuButton variant="danger" onClick={() => { onDelete(vwap.id); onClose() }}>DELETE</MenuButton>
      </MenuActions>
    </ChartMenu>
  )
}
